import { useMemo } from 'react'

import { Button, Typography, Skeleton } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { useUser, countUserSites } from 'store'
import { useNavigate } from 'router'
import useFocus from 'components/AuthenticatedLayout/Header/AccountsPerimeter/use-focus'

import AccountPanelTree from './AccountPanelTree'
import useBuildings from '../use-buildings'
import AccountPanelTreeAutocomplete from './AccountPanelTreeAutocomplete'


const OPERATION_FIND_SITE = gql`
  query GetSite($sf_id: String!) {
    site: findSite(sf_id: $sf_id) {
      account_parent {
        sf_id
        name
      }
      sf_id
      name
      id
      equipment {
        available_level
      }
    }
  }
 `

const AccountPanel = ({sites}) => {
  const {t} = useTranslation()
  const { focusId } = useParams()
  const [user] = useUser()
  const navigate = useNavigate()
  const {onBlur} = useFocus()
  const { loading, buildings } = useBuildings(focusId)
  const hasFocus = useMemo(() => {
    return sites.some(({sf_id}) => sf_id === focusId)
  }, [sites, focusId])
  // si le focus n'est pas dans la sélection nous n'avons pas le site
  const siteQuery = useQuery(OPERATION_FIND_SITE, {
    variables: {
      sf_id: focusId
    },
    skip: hasFocus
  })
  const currentSite = useMemo(() => {
    return hasFocus
      ? sites.find(site => site.sf_id === focusId)
      : siteQuery.data?.site
  }, [sites, focusId, hasFocus, siteQuery])
  const isSiteDown = useMemo(() => {
    return currentSite?.equipment?.available_level === 'down'
  }, [currentSite])
  const showBackButton = useMemo(() => {
    return countUserSites(user) > 1
  }, [user])

  return (
    <Stack
      gap={2}
      direction="column"
      sx={{
        overflow: 'scroll',
        justifyContent: 'flex-start',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
      }}
      minWidth={300}
      border={'1px solid #E5E7EB'}
      borderRight={0}
      borderRadius={'24px'}
      height={'100%'}
      p={1}
    >
      {showBackButton && (
        <Stack alignItems={'flex-start'} ml={1}>
          <Button
            onClick={() => {
              onBlur()
              navigate({pathname: '/monitoring/map'})
            }}
            variant="text"
            sx={{
              p: 0,
              display: 'flex',
              gap: 1,
              backgroundColor: 'none',
              fontSize: 12,
              fontWeight: 400,
              '&: hover': {
                background: 'none'
              }
            }}
          >
            {<i className="fa-light fa-arrow-left "></i>}
            {t('supervision.link_card_global')}
          </Button>
        </Stack>
      )}
      <Stack ml={1.5} gap={1}>
        <Typography
          sx={{
            gap: 1,
            color: 'content.primary',
            fontSize: '14px',
            lineHeight: '18px',
            fontWeight: 500,
            ...(!showBackButton && { mt: 1.2 })
          }}
        >
          {currentSite?.account_parent?.name}
        </Typography>
        {
          currentSite 
            ? <AccountPanelTreeAutocomplete selectedSite={currentSite} />
            : <Skeleton variant="rectangular" width={'100%'} height={40} />
        }
      </Stack>
      {!loading ? (
        <AccountPanelTree
          buildings={buildings}
          onMapClick={(node) => {
            navigate({
              pathname: `/monitoring/map/${focusId}/${node.data.buildingId}/${node.id}`
            })
          }}
          isSiteDown={isSiteDown}
        />
      ) : (
        <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
          <Typography variant={'caption'}>{t('ui_elements.loading_building')}</Typography>
        </Box>
      )}
    </Stack>
  )
}

export default AccountPanel